<template>
  <v-sheet class="px-3 d-flex flex-column fill-height">
    <v-overlay :value="loaderOverlay" :absolute="true">
      <v-row justify="center">
        <v-progress-circular indeterminate size="64"></v-progress-circular>
      </v-row>
    </v-overlay>

    <v-row class="header-sticky flex-grow-1">
      <v-card :color="cardColor" min-width="640" class="w-100 d-flex flex-column flex-grow-1" :width="'100%'" elevation="0">
        <v-card-title>
          <v-row justify="start" no-gutters>
            <v-icon large class="pr-2">mdi-chart-timeline</v-icon>
            <h3>{{ $t("workTimeReport") }}</h3>
          </v-row>
        </v-card-title>

        <v-card-text class="d-flex flex-column flex-grow-1">
          <v-row class="my-3" no-gutters>
            <v-btn class="mr-2" :color="getButtonSelectionState('daily')" @click="setButtonSelection('daily')">
              {{ $t("dailyReport") }}
            </v-btn>
            <v-btn class="mx-2" :color="getButtonSelectionState('weekly')" @click="setButtonSelection('weekly')">
              {{ $t("weeklyReport") }}
            </v-btn>
            <v-btn class="mx-2" :color="getButtonSelectionState('monthly')" @click="setButtonSelection('monthly')">
              {{ $t("monthlyReport") }}
            </v-btn>
            <v-btn class="mx-2" :color="getButtonSelectionState('yearly')" @click="setButtonSelection('yearly')">
              {{ $t("yearlyReport") }}
            </v-btn>

            <v-spacer />

            <v-row v-if="displayData.length > 0" class="pa-0 ma-0" align="center" justify="end" no-gutters>
              <v-tooltip bottom color="primary">
                <template slot="activator" slot-scope="{ on }">
                  <v-icon v-on="on" @click="resetFilterCache" class="pr-4"> mdi-arrow-u-left-top </v-icon>
                </template>
                <span>{{ $t("resetFilter") }}</span>
              </v-tooltip>

              <v-btn prepend-icon="mdi-filter-outline" @click="handleSelectionClick" class="primary" color="white--text">
                {{ $t("filterColumns") }}
              </v-btn>

              <v-divider class="mx-3" vertical />

              <div class="d-flex page-size-wrapper">
                <v-select class="page-size-select" :label="$t('pageSize')" :items="pageSizes" v-model="pageSize" outlined hide-details dense />
              </div>

              <v-divider class="mx-3" vertical />

              <v-tooltip bottom color="primary" class="white-text">
                <template v-slot:activator="{ on }">
                  <v-btn v-on="on" icon @click="handleCSVExport">
                    <v-icon large>mdi-file-export-outline</v-icon>
                  </v-btn>
                </template>
                <span>{{ $t("exportBtn") }}</span>
              </v-tooltip>
            </v-row>
          </v-row>

          <v-divider class="my-4" />

          <v-row class="pa-0 ma-0 align-center">
            <v-col class="pl-1">
              <v-autocomplete
                v-model="selectedTeams"
                :items="teams"
                item-text="Team"
                color="primary"
                chips
                elevated
                prepend-icon="mdi-account-group"
                :label="$t('teams')"
                multiple
                hide-details
                dense
              >
                <template v-slot:selection="data">
                  <v-chip
                    v-if="data.index < 3"
                    v-bind="data.attrs"
                    :input-value="data.selected"
                    close
                    @click="data.select"
                    @click:close="removeSelectedTeam(data.item)"
                  >
                    {{ data.item }}
                  </v-chip>
                  <span v-if="data.index === selectedTeams.length - 1 && selectedTeams.length > 3">
                    {{ ` (+${selectedTeams.length - 3})` }}
                  </span>
                </template>

                <template v-slot:item="data">
                  <v-list-item-content>
                    <v-list-item-title>{{ data.item }}</v-list-item-title>
                  </v-list-item-content>
                </template>
              </v-autocomplete>
            </v-col>

            <v-col>
              <v-autocomplete
                v-if="currentView !== 'perDepartment'"
                v-model="selectedEmployees"
                :items="filteredEmployees"
                item-text="name"
                color="primary"
                chips
                elevated
                :label="$t('employees')"
                multiple
                prepend-icon="mdi-account"
                hide-details
                dense
                return-object
              >
                <template v-slot:prepend-item>
                  <v-list-item ripple @mousedown.prevent @click="handleSelectAll" dense hide-details>
                    <v-list-item-action>
                      <v-icon :color="selectedEmployees.length > 0 ? 'red darken-4' : ''">
                        {{ selectedEmployees.length > 0 ? "mdi-checkbox-marked" : "mdi-checkbox-blank-outline" }}
                      </v-icon>
                    </v-list-item-action>
                    <v-list-item-content>
                      <v-list-item-title>{{ $t("selectAll") }}</v-list-item-title>
                    </v-list-item-content>
                  </v-list-item>
                  <v-divider class="mt-2" />
                </template>

                <template v-slot:selection="data">
                  <v-tooltip bottom color="primary">
                    <template v-slot:activator="{ on }">
                      <v-chip
                        v-on="on"
                        v-if="data.index < 1"
                        v-bind="data.attrs"
                        :input-value="data.selected"
                        close
                        @click="data.select"
                        @click:close="removeSelectedEmployee(data.item)"
                      >
                        {{ data.item.name }}
                      </v-chip>
                    </template>
                    <span>{{ selectedEmployees.map((e) => e.name).join(", \n") }}</span>
                  </v-tooltip>
                  <span v-if="data.index === selectedEmployees.length - 1 && selectedEmployees.length > 1">
                    {{ ` (+${selectedEmployees.length - 1})` }}
                  </span>
                </template>

                <template v-slot:item="data">
                  <v-list-item-content>
                    <v-list-item-title>{{ data.item.name }}</v-list-item-title>
                    <v-list-item-subtitle>
                      {{ setTeamsForAutocomplete(data.item.teams) }}
                    </v-list-item-subtitle>
                  </v-list-item-content>
                </template>
              </v-autocomplete>
            </v-col>

            <v-col style="display: inline-flex" class="mx-2">
              <v-icon>mdi-calendar-multiselect-outline</v-icon>
              <DatePicker v-model="date.from" dense :mydate1="date.from" :label="$t('dateFrom')" />
            </v-col>

            <v-col style="display: inline-flex" class="mx-2">
              <v-icon>mdi-calendar-multiselect-outline</v-icon>
              <DatePicker v-model="date.until" dense :mydate1="date.until" :label="$t('dateUntil')" />
            </v-col>

            <v-btn v-if="!loaderOverlay" class="ml-3 mr-1" color="primary" @click="fetch">
              {{ $t("refresh") }}
            </v-btn>
            <v-btn v-else class="mx-3" color="primary" icon>
              <v-icon class="rotate">mdi-refresh</v-icon>
            </v-btn>
          </v-row>

          <v-divider class="my-4" />

          <v-row class="pa-0 ma-0 flex-grow-1">
            <v-col class="pa-0 d-flex flex-column">
              <v-sheet v-if="displayData.length > 0" class="report-scroll" :style="{ height: listHeight + 'px', maxHeight: listHeight + 'px' }">
                <DynamicList
                  class="flex-grow-1"
                  :searchTerm="searchTerm"
                  :pageSize="pageSize"
                  :data="displayData"
                  :styles="styles"
                  :headers="headers"
                  :headerTypes="headerTypes"
                  :columnTypes="columnTypes"
                  :columnSelection="columnSelection"
                />
              </v-sheet>

              <v-card v-else-if="!loaderOverlay" class="mx-0 mt-2 py-5 flex-grow-1 d-flex align-center justify-center">
                <NoData />
              </v-card>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>
    </v-row>

    <v-row class="wrapper mt-5" cols="2" style="position: fixed" :style="{ top: position.top, left: position.left }">
      <v-card v-if="selectionIsOpen" :color="$vuetify.theme.dark ? '#1E1E1E' : 'grey lighten-2'" width="unset" class="mr-10 pa-5">
        <ColumnControl
          v-click-outside="handleOutsideClick"
          id="column-control"
          :headers="headers"
          :columnSelection="columnSelection"
          @update="updateSelectedColumns"
        />
      </v-card>
    </v-row>

    <LoadingInfo :state="loadingInfo.state" :message="loadingInfo.message" />
  </v-sheet>
</template>

<script>
import DynamicList from "../../components/DynamicList/DynamicList.vue";
import ColumnControl from "../../components/DynamicList/ColumnControl.vue";
import DatePicker from "../../components/SimpleComponents/DatePicker.vue";
import NoData from "../../components/GeneralReporting/WorkTimeReport/NoData.vue";
import LoadingInfo from "../../components/Global/LoadingInfo.vue";

import headerTypes from "../../components/GeneralReporting/WorkTimeReport/headerTypes.js";
import headerStyles from "../../components/GeneralReporting/WorkTimeReport/headerStyles.js";

import { COLUMN_TYPES } from "../../enums/ColumnTypes.js";
import { mapGetters } from "vuex";

export default {
  name: "WorkTimeReport",
  route: "TimeReport",
  components: {
    DynamicList,
    ColumnControl,
    DatePicker,
    NoData,
    LoadingInfo
  },
  data: (vm) => ({
    loaderOverlay: false,
    loadingInfo: {
      state: false,
      message: ""
    },
    loadingAnimationIterations: 0,
    loadingInterval: null,
    searchTerm: "",
    pageSize: 10,
    pageSizes: [10, 25, 50],
    columnSelection: [],
    data: [],
    displayData: [],
    optionalHeaders: {
      daily: ["U_employeeId", "U_weekday", "U_ratio", "U_extID", "teams"],
      weekly: ["U_employeeId", "U_ratio", "U_extID", "teams"],
      monthly: ["U_employeeId", "U_ratio", "U_extID", "teams"],
      yearly: ["U_employeeId", "U_ratio", "U_extID", "teams"]
    },
    headerTypes: headerTypes,
    styles: headerStyles,
    columnTypes: [
      {
        column: "month",
        value: COLUMN_TYPES.MONTH
      }
    ],
    position: {
      top: undefined,
      left: undefined
    },
    selectionIsOpen: false,
    selectedEmployees: [],
    selectedTeams: [],
    date: {
      from: vm.getDate(),
      until: vm.getDate()
    },
    currentView: "daily",
    listHeight: 0
  }),
  async created() {
    if (localStorage.getItem("generalReportFromDate") != null) this.date.from = localStorage.getItem("generalReportFromDate");
    if (localStorage.getItem("generalReportUntilDate") != null) this.date.until = localStorage.getItem("generalReportUntilDate");

    if (localStorage.getItem("generalReportEmployeesFilter") != null)
      this.selectedEmployees = JSON.parse(localStorage.getItem("generalReportEmployeesFilter") ?? "[]");
    if (localStorage.getItem("generalReportTeamsFilter") != null) this.selectedTeams = JSON.parse(localStorage.getItem("generalReportTeamsFilter") ?? "[]");

    await this.fetch();
    await this.reProcessLines();
  },
  mounted() {
    this.updateListHeight();
    window.addEventListener("resize", this.updateListHeight);
    this.$nextTick(() => {
      const drawer = document.querySelector(".v-navigation-drawer");
      if (drawer) drawer.addEventListener("transitionend", this.updateListHeight);
    });
  },
  beforeDestroy() {
    window.removeEventListener("resize", this.updateListHeight);
    const drawer = document.querySelector(".v-navigation-drawer");
    if (drawer) drawer.removeEventListener("transitionend", this.updateListHeight);
  },
  computed: {
    selectedUser() {
      if (this.isOneEmployee) {
        return this.employees.find((employee) => employee.SAPEmpID === this.selectedEmployees[0]?.SAPEmpID);
      } else {
        return this.selectedEmployees[0];
      }
    },
    isOneEmployee() {
      const employee = this.displayData[0]?.U_employeeId;
      if (!employee) return false;
      return this.displayData.every((entry) => entry.U_employeeId === employee);
    },
    userData() {
      return this.displayData.filter((entry) => entry.U_employeeId === this.selectedEmployees[0]?.SAPEmpID);
    },
    cardColor() {
      return this.$vuetify.theme.dark ? "#2E2E2E" : "#FFFFFF";
    },
    headers() {
      return [...new Set(this.displayData.map((entry) => Object.keys(entry)).flat())];
    },
    filteredEmployees() {
      if (this.selectedTeams.length === 0) return this.employees;
      if (this.selectedTeams.find((team) => team === this.$t("all"))) return this.employees;
      return this.employees.filter((employee) => {
        return this.selectedTeams.some((team) => employee.teams.find(({ Team }) => Team === team));
      });
    },
    ...mapGetters({
      teams: "users/teams",
      employees: "users/employeeList"
    })
  },
  watch: {
    async currentView(val) {
      this.loaderOverlay = true;
      if (val === "perDepartment") {
        this.selectedEmployees = [];
      }
      await this.handleCurrentView(val);
      this.loaderOverlay = false;
    },
    displayData() {
      this.updateColumnSelection(this.headers);
    },
    loadingInfo: {
      handler(val) {
        if (this.loadingInterval) return;
        if (val.message.endsWith("...")) {
          this.animateLoadingInfo();
        } else {
          this.stopAnimation();
        }
      },
      deep: true
    },
    "date.from": function (val) {
      localStorage.setItem("generalReportFromDate", val);
    },
    "date.until": function (val) {
      localStorage.setItem("generalReportUntilDate", val);
    },
    selectedTeams(val) {
      localStorage.setItem("generalReportTeamsFilter", JSON.stringify(val));
    },
    selectedEmployees(val) {
      localStorage.setItem("generalReportEmployeesFilter", JSON.stringify(val));
    }
  },
  methods: {
    updateListHeight() {
      const bp = this.$vuetify && this.$vuetify.breakpoint ? this.$vuetify.breakpoint : null;

      const viewportHeight = bp && bp.height ? bp.height : window.innerHeight || 900;
      const viewportWidth = bp && bp.width ? bp.width : window.innerWidth || 1280;

      let offset = 420;

      const drawer = document.querySelector(".v-navigation-drawer");
      const drawerWidth = drawer ? drawer.getBoundingClientRect().width : 0;
      const menuOpen = drawerWidth > 100;

      const contentWidth = viewportWidth - (menuOpen ? drawerWidth : 0);

      const needsExtraSpace = menuOpen && contentWidth < 1150;

      if (needsExtraSpace) {
        offset = 460;
      }

      this.listHeight = viewportHeight - offset;
    },
    handleSelectAll() {
      if (this.selectedEmployees.length === this.filteredEmployees.length) {
        this.selectedEmployees = [];
      } else {
        this.selectedEmployees = this.filteredEmployees;
      }
    },
    animateLoadingInfo() {
      const interval = setInterval(() => {
        if (!this.loadingInfo.message) return this.stopAnimation();
        const parts = this.loadingInfo.message.split(".");
        let dots = "";
        for (let i = 0; i < this.loadingAnimationIterations % 4; i++) {
          dots += ".";
        }
        this.loadingInfo.message = parts[0] + dots;
        this.loadingAnimationIterations++;
      }, 500);
      this.loadingInterval = interval;
    },
    stopAnimation() {
      if (!this.loadingInterval) return;
      clearInterval(this.loadingInterval);
      this.loadingInterval = null;
    },
    getFullTimeStamp() {
      return `${this.getDate()}_${this.getTime()}`;
    },
    downloadCSVFile(csvString) {
      const fileName = `COBI-Time_Reports_WorkTime_${this.date.from}-${this.date.until}.csv`;
      const blob = new Blob([csvString], { type: "text/csv" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = fileName;
      a.click();
      URL.revokeObjectURL(url);
    },
    handleCSVExport() {
      const delimiter = ";";
      const filterData = [];
      for (const item of this.displayData) {
        const object = {};
        for (const [key, value] of Object.entries(item)) {
          if (this.columnSelection.find((column) => column === key) != null) object[key] = value;
        }
        filterData.push(object);
      }
      const selectedColumn = Object.keys(filterData[0]);
      const headers = selectedColumn.map((header) => this.$t(header));
      const stringifiedElements = filterData.map((element) => {
        if (element.U_ratio != null && element.U_ratio.startsWith("NaN")) element.U_ratio = "";
        return Object.values(element)
          .map((elem) => {
            elem = elem.toString().replace(/\./g, ",");
            return elem[0] === "-" || elem.split("").includes(":") ? ` ${elem}` : elem;
          })
          .join(delimiter);
      });
      const CSVOutput = `${headers.join(delimiter)}\n${stringifiedElements.join("\n")}`;
      this.downloadCSVFile(CSVOutput);
    },
    async handleCurrentView(val) {
      switch (val) {
        case "perUser":
          this.displayData = await this.sumUpUserData();
          break;
        case "perDepartment":
          this.displayData = await this.sumUpDepartmentData();
          break;
        default:
          await this.reProcessLines();
          break;
      }
      this.$nextTick(this.updateListHeight);
    },
    async sumUpUserData() {
      let res = await this.$store.dispatch("reporting/process", {
        data: this.data,
        employees: this.getSelectedEmployees().map((employee) => ({
          SAPEmpID: employee.SAPEmpID,
          name: employee.name,
          extID: employee.extID,
          teams: employee.teams
        })),
        type: "yearly"
      });
      const users = this.getSelectedEmployees().map((employee) => employee.name);
      const newData = [];
      for (const user of users) {
        const [head, ...tail] = res.filter((entry) => entry.U_name === user).sort((a, b) => +b.U_date - +a.U_date);
        if (!head) continue;
        let sum = {
          U_name: head.U_name,
          U_workTime: head.U_workTime,
          U_breakTime: head.U_breakTime,
          U_projectTime: head.U_projectTime,
          U_totalTime: head.U_totalTime,
          U_requiredTime: head.U_requiredTime,
          U_difference: head.U_difference,
          U_ratio: head.U_ratio,
          U_average: head.U_average
        };
        if (tail?.length > 0) {
          sum = tail.reduce((acc, entry) => {
            acc.U_workTime = this.addTime(acc.U_workTime + entry.U_workTime);
            acc.U_breakTime = this.addTime(acc.U_breakTime + entry.U_breakTime);
            acc.U_projectTime = this.addTime(acc.U_projectTime + entry.U_projectTime);
            acc.U_difference = this.addTime(acc.U_difference + entry.U_difference);
            acc.U_average = this.addTime(acc.U_average, entry.U_average);
            return acc;
          }, head);
        }
        sum.U_average = this.minutesToString(this.stringToMinutes(sum.U_average) / res.length);
        sum.U_ratio = `${((parseInt(sum.U_workTime.split(":")[0]) / parseInt(sum.U_requiredTime.split(":")[0])) * 100).toFixed(2)}%`;
        newData.push(sum);
      }
      return newData;
    },
    async sumUpDepartmentData() {
      let res = await this.$store.dispatch("reporting/process", {
        data: this.data,
        employees: this.getSelectedEmployees().map((employee) => ({
          SAPEmpID: employee.SAPEmpID,
          name: employee.name,
          extID: employee.extID,
          teams: employee.teams
        })),
        type: "yearly"
      });
      let departments = this.selectedTeams.length === 0 ? this.teams : this.selectedTeams;
      if (departments.find((team) => team === this.$t("all"))) departments = this.teams;
      const departmentData = new Map();
      for (const department of departments) {
        const members = this.employees.filter((employee) => employee.teams.some(({ Team }) => Team === department));
        if (members.length === 0) continue;
        const [head, ...tail] = res.filter((entry) => members.some((employee) => employee.SAPEmpID === entry.U_employeeId));
        if (!head) continue;
        let sum = {
          U_department: department,
          U_memberCount: members.length,
          U_workTime: head.U_workTime,
          U_breakTime: head.U_breakTime,
          U_projectTime: head.U_projectTime,
          U_requiredTime: head.U_requiredTime,
          U_totalTime: head.U_totalTime,
          U_difference: head.U_difference,
          U_ratio: head.U_ratio,
          U_average: head.U_average
        };
        if (tail?.length > 0) {
          sum = tail.reduce((acc, entry) => {
            acc.U_workTime = this.addTime(acc.U_workTime, entry.U_workTime);
            acc.U_breakTime = this.addTime(acc.U_breakTime, entry.U_breakTime);
            acc.U_projectTime = this.addTime(acc.U_projectTime, entry.U_projectTime);
            acc.U_requiredTime = this.addTime(acc.U_requiredTime, entry.U_requiredTime);
            acc.U_totalTime = this.addTime(acc.U_totalTime, entry.U_totalTime);
            acc.U_difference = this.addTime(acc.U_difference, entry.U_difference);
            acc.U_average = this.addTime(acc.U_average, entry.U_average);
            return acc;
          }, sum);
        }
        sum.U_average = this.minutesToString(this.stringToMinutes(sum.U_average) / res.length);
        sum.U_ratio = `${((parseInt(sum.U_workTime.split(":")[0]) / parseInt(sum.U_requiredTime.split(":")[0])) * 100).toFixed(2)}%`;
        departmentData.set(department, sum);
      }
      return [...departmentData.values()];
    },
    stringToMinutes(time) {
      const [hours, minutes] = time.split(":").map((e) => parseInt(e, 10));
      return hours * 60 + minutes;
    },
    minutesToString(total) {
      const hours = Math.floor(total / 60);
      const minutes = Math.floor(total % 60);
      return `${hours.toString().padStart(2, 0)}:${minutes.toString().padStart(2, 0)}`;
    },
    addTime(timeA, timeB = "00:00") {
      const [hoursA, minutesA] = timeA.split(":").map((e) => parseInt(e, 10));
      const [hoursB, minutesB] = timeB.split(":").map((e) => parseInt(e, 10));
      if (minutesA + minutesB >= 60) {
        return `${(hoursA + hoursB + 1).toString().padStart(2, 0)}:${((minutesA + minutesB) % 60).toString().padStart(2, 0)}`;
      } else {
        return `${(hoursA + hoursB).toString().padStart(2, 0)}:${(minutesA + minutesB).toString().padStart(2, 0)}`;
      }
    },
    getSelectedEmployees() {
      const employeesSelected = this.selectedEmployees.length > 0;
      const teamsSelected = this.selectedTeams.length > 0;
      switch (true) {
        case employeesSelected && teamsSelected:
          return this.selectedEmployees;
        case employeesSelected:
          return this.selectedEmployees;
        case teamsSelected:
          return this.filteredEmployees;
        default:
          return this.employees;
      }
    },
    updateColumnSelection(selection) {
      const newHeaders = selection.filter((header) => {
        if (!this.optionalHeaders[this.currentView]) return true;
        return !this.optionalHeaders[this.currentView].includes(header);
      });
      const headerFromLocalStore = JSON.parse(localStorage.getItem("generalReportFilter") ?? "[]");
      if (headerFromLocalStore.length !== 0) this.columnSelection = headerFromLocalStore;
      else this.columnSelection = newHeaders;
    },
    async getWorkTimeInfoForDay(userId, date) {
      const data = {
        userId,
        date
      };
      return await this.$store
        .dispatch("users/timeAccounts/getWorkTimeInfoForDay", data)
        .then((resp) => {
          if (resp.data.success) {
            return resp.data.data;
          } else this.$eventHub.$emit("showError", resp.data.error);
        })
        .catch(console.log);
    },
    async fetch() {
      this.loadingInfo.state = true;
      this.loadingInfo.message = `${this.$t("fetchingData")}...`;
      await Promise.all([this.getTeams(), this.getUserList()]);
      const res = await this.$store.dispatch("reporting/getBookingLinesForUsers", {
        users: this.getSelectedEmployees().map((employee) => ({
          SAPEmpID: employee.SAPEmpID,
          name: employee.name
        })),
        dateFrom: this.date.from,
        dateTo: this.date.until
      });
      this.loadingInfo.message = `${this.$t("processingData")}...`;
      this.data = res;
      await this.handleCurrentView(this.currentView);
      this.updateColumnSelection(this.headers);
      this.loadingInfo.state = false;
      this.loadingInfo.message = "";
      this.$nextTick(this.updateListHeight);
    },
    async reProcessLines() {
      this.displayData = [];
      let res = await this.$store.dispatch("reporting/process", {
        data: this.data,
        employees: this.getSelectedEmployees().map((employee) => ({
          SAPEmpID: employee.SAPEmpID,
          name: employee.name,
          extID: employee.extID,
          teams: employee.teams
        })),
        type: this.currentView
      });
      res = res.map((entry) => {
        if (entry.U_weekday) entry.U_weekday = this.$t(entry.U_weekday);
        return entry;
      });
      if (!res || res.length === 0) {
        this.displayData = [];
        return;
      }
      if (Object.keys(res[0] ?? []).includes("U_ratio")) {
        res = res.map((entry) => {
          entry.U_ratio = `${entry.U_ratio ?? NaN}%`;
          return entry;
        });
      }
      if (this.currentView && this.currentView === "monthly") {
        res = res.map((entry) => {
          entry.month = this.translateMonth(entry);
          return entry;
        });
      }
      this.displayData = this.filterLines(res);
      this.$nextTick(this.updateListHeight);
    },
    preProcessLines(data) {
      const filtered = this.filterSelectedEmployees(this.filterLines(data));
      const columns = ["U_name", "U_weekday", "U_date", "U_time", "U_employeeId", "U_status", "U_project", "U_businessTrip"];
      const ordered = this.reorderColumns(filtered, columns);
      const processed = ordered.map((entry) => {
        entry = this.translateBusinessTripStatus(entry);
        entry = this.formatDate(entry);
        entry = this.translateWorkStatus(entry);
        return entry;
      });
      return processed;
    },
    translateWeekday(entry) {
      return this.$t(entry.U_weekday);
    },
    translateMonth(entry) {
      return this.$t(entry.month);
    },
    translateBusinessTripStatus(entry) {
      entry.U_businessTrip = entry.U_businessTrip === 1 ? this.$t("yes") : this.$t("no");
      return entry;
    },
    formatDate(entry) {
      entry.U_date = entry.U_date.split("T")[0];
      return entry;
    },
    translateWorkStatus(entry) {
      entry.U_status = this.$t(entry.U_status);
      return entry;
    },
    reorderColumns(data, columns) {
      return data.map((entry) => {
        return columns.reduce((obj, key) => {
          obj[key] = entry[key];
          return obj;
        }, {});
      });
    },
    filterLines(data) {
      const removeKeys = ["U_deductBreakTimes", "U_cutWorkTime"];
      return data.map((entry) => {
        return Object.keys(entry)
          .filter((key) => !removeKeys.includes(key))
          .reduce((obj, key) => {
            obj[key] = entry[key];
            return obj;
          }, {});
      });
    },
    filterSelectedEmployees(data) {
      const selectedEmployees = this.getSelectedEmployees();
      return data.filter((entry) => selectedEmployees.some((employee) => employee.SAPEmpID === entry.U_employeeId));
    },
    getTime() {
      const date = new Date();
      const hours = date.getHours();
      const minutes = date.getMinutes();
      const seconds = date.getSeconds();
      return [hours, minutes, seconds].map((e) => e.toString().padStart(2, "0")).join(":");
    },
    getDate() {
      const date = new Date();
      const day = date.getDate();
      const month = date.getMonth() + 1;
      const year = date.getFullYear();
      return [year, month, day].map((e) => e.toString().padStart(2, "0")).join("-");
    },
    setTeamsForAutocomplete(teams) {
      return teams.map((team) => team.Team).join(", ");
    },
    removeSelectedEmployee(item) {
      const index = this.selectedEmployees.findIndex((employee) => employee.SAPEmpID === item.SAPEmpID);
      if (index >= 0) this.selectedEmployees.splice(index, 1);
    },
    removeSelectedTeam(item) {
      const index = this.selectedTeams.findIndex((team) => team === item);
      if (index >= 0) this.selectedTeams.splice(index, 1);
    },
    async getTeams() {
      await this.$store.dispatch("users/getTeams");
    },
    async getUserList() {
      if (this.$store.getters["user/user"].userGroupRights.canAccessGeneralReporting) await this.$store.dispatch("users/getEmployees");
      else if (this.$store.getters["user/user"].userGroupRights.canAccessGeneralReportingTeam) await this.$store.dispatch("users/getEmployeesFromSameTeam");
    },
    handleOutsideClick() {
      this.selectionIsOpen = false;
      this.position = { top: undefined, left: undefined };
    },
    updateSelectedColumns(update) {
      localStorage.setItem("generalReportFilter", JSON.stringify(update));
      this.columnSelection = structuredClone(update);
    },
    handleSelectionClick(event) {
      this.selectionIsOpen = !this.selectionIsOpen;
      const clickLeft = event.clientX;
      const clickTop = event.clientY;
      const card = document.getElementById("column-control");
      const cardWidth = card?.style.width || 180;
      const newLeft = clickLeft - cardWidth;
      this.position.top = `${clickTop}px`;
      this.position.left = `${newLeft}px`;
    },
    getButtonSelectionState(selection) {
      return this.currentView === selection ? "primary" : "";
    },
    setButtonSelection(selection) {
      this.currentView = selection;
    },
    resetFilterCache() {
      this.selectedEmployees = [];
      this.selectedTeams = [];
      this.date.from = this.getDate();
      this.date.until = this.getDate();
      localStorage.removeItem("generalReportFilter");
      localStorage.removeItem("generalReportFromDate");
      localStorage.removeItem("generalReportUntilDate");
      localStorage.removeItem("generalReportEmployeesFilter");
      localStorage.removeItem("generalReportTeamsFilter");
      this.fetch();
    }
  }
};
</script>

<style scoped>
.rotate {
  animation: rotation 2s infinite linear;
}

@keyframes rotation {
  from {
    transform: rotate(0deg);
  }

  to {
    transform: rotate(359deg);
  }
}

.report-scroll {
  overflow-y: auto;
  overflow-x: hidden;
  display: flex;
  flex-direction: column;
  min-height: 0;
}

.report-scroll > * {
  flex: 1 1 auto;
  min-height: 0;
}

.wrapper {
  z-index: 1000;
}

.page-size-wrapper {
  max-width: 140px;
}

.page-size-select .v-input__control {
  min-width: 0;
}

@media (max-width: 1264px) {
  .page-size-wrapper {
    max-width: 120px;
  }
}
</style>
